<template>
  <div class="admin-management">
    <div class="page-header">
      <h1>Admin Management</h1>
    </div>

    <!-- Search -->
    <div class="search-bar">
      <a-input-search v-model:value="searchQuery" placeholder="Cari nama atau email..." style="width: 300px"
        @search="loadAdmins" allow-clear />
      <a-button type="primary" danger size="large" style="border-radius: 50px; font-weight: 700"
        @click="showCreateModal">
        <plus-outlined /> TAMBAH ADMIN
      </a-button>
    </div>

    <!-- Table -->
    <a-table :columns="columns" :data-source="admins" :loading="loading" :pagination="pagination"
      @change="handleTableChange" row-key="id">
      <template #action="{ record }">
        <div class="action-buttons">
          <a-button type="primary" danger shape="circle" size="large" @click="showEditModal(record)">
            <edit-outlined />
          </a-button>
          <a-popconfirm title="Yakin ingin menghapus admin ini?" ok-text="Ya" cancel-text="Tidak"
            @confirm="deleteAdmin(record)">
            <a-button shape="circle" size="large" class="btn-delete">
              <delete-outlined />
            </a-button>
          </a-popconfirm>
        </div>
      </template>
    </a-table>

    <!-- Create/Edit Modal -->
    <a-modal :title="modalTitle" :visible="modalVisible" :confirm-loading="modalLoading" @ok="handleModalOk"
      @cancel="handleModalCancel">
      <a-form :model="form" layout="vertical" ref="formRef">
        <a-form-item label="Nama" name="name" :rules="[{ required: true, message: 'Nama wajib diisi' }]">
          <a-input v-model:value="form.name" placeholder="Masukkan nama" />
        </a-form-item>

        <a-form-item label="Email" name="email" :rules="[
          { required: true, message: 'Email wajib diisi' },
          { type: 'email', message: 'Email tidak valid' },
        ]">
          <a-input v-model:value="form.email" placeholder="Masukkan email" />
        </a-form-item>

        <a-form-item v-if="!editingAdmin" label="Password" name="password" :rules="[
          { required: true, message: 'Password wajib diisi' },
          { min: 8, message: 'Password minimal 8 karakter' },
        ]">
          <a-input-password v-model:value="form.password" placeholder="Masukkan password" />
        </a-form-item>

      </a-form>
      <template #footer>
        <div class="confirm-footer d-flex justify-center gap-x-4 mt-8">
          <button class="btn-footer btn-confirm-cancel" @click="handleModalCancel">Kembali</button>
          <button class="btn-footer btn-confirm-ok" @click="handleModalOk">
            Simpan
          </button>
        </div>
        <!-- <a-button key="back" @click="handleModalCancel">Kembali</a-button>
        <a-button key="submit" type="primary" :loading="modalLoading" @click="handleModalOk">Simpan</a-button> -->
      </template>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons-vue'
import { mobileCmsAdminAPI } from '@/services/axios/perki/mobileCms'

export default {
  name: 'AdminManagement',
  components: { EditOutlined, DeleteOutlined, PlusOutlined },
  setup() {
    const loading = ref(false)
    const admins = ref([])
    const searchQuery = ref('')
    const modalVisible = ref(false)
    const modalLoading = ref(false)
    const editingAdmin = ref(null)
    const formRef = ref(null)

    const form = reactive({
      name: '',
      email: '',
      password: '',
    })

    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: total => `Total ${total} items`,
    })

    const sortParams = reactive({
      field: '',
      order: '',
    })

    const columns = [
      { title: 'Nama', dataIndex: 'name', key: 'name', sorter: true },
      { title: 'Email', dataIndex: 'email', key: 'email', sorter: true },
      { title: 'Action', key: 'action', width: 150, slots: { customRender: 'action' } },
    ]

    const modalTitle = computed(() => (editingAdmin.value ? 'Edit Admin' : 'Tambah Admin'))

    const loadAdmins = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.value.current,
          pageSize: pagination.value.pageSize,
          q: searchQuery.value,
        }

        if (sortParams.field && sortParams.order) {
          params.sortBy = sortParams.field
          params.sortOrder = sortParams.order === 'ascend' ? 'ASC' : 'DESC'
        }

        const response = await mobileCmsAdminAPI.getAll(params)
        const result = response.data.result
        admins.value = result.data || []
        pagination.value.total = result.total || 0
      } catch (error) {
        message.error('Gagal memuat data admin')
        console.error(error)
      } finally {
        loading.value = false
      }
    }

    const handleTableChange = (pag, filters, sorter) => {
      pagination.value.current = pag.current
      pagination.value.pageSize = pag.pageSize
      if (sorter && sorter.field) {
        sortParams.field = sorter.field
        sortParams.order = sorter.order
      } else {
        sortParams.field = ''
        sortParams.order = ''
      }
      loadAdmins()
    }

    const resetForm = () => {
      form.name = ''
      form.email = ''
      form.password = ''
    }

    const showCreateModal = () => {
      editingAdmin.value = null
      resetForm()
      modalVisible.value = true
    }

    const showEditModal = record => {
      editingAdmin.value = record
      form.name = record.name
      form.email = record.email
      modalVisible.value = true
    }

    const handleModalCancel = () => {
      modalVisible.value = false
      editingAdmin.value = null
      resetForm()
    }

    const handleModalOk = async () => {
      try {
        await formRef.value.validate()
      } catch {
        return
      }

      modalLoading.value = true
      try {
        const payload = {
          name: form.name,
          email: form.email,
        }

        if (editingAdmin.value) {
          await mobileCmsAdminAPI.update(editingAdmin.value.id, payload)
          message.success('Admin berhasil diupdate')
        } else {
          payload.password = form.password
          await mobileCmsAdminAPI.create(payload)
          message.success('Admin berhasil dibuat')
        }
        modalVisible.value = false
        editingAdmin.value = null
        resetForm()
        loadAdmins()
      } catch (error) {
        const msg = error.response?.data?.result || 'Terjadi kesalahan'
        message.error(msg)
      } finally {
        modalLoading.value = false
      }
    }

    const deleteAdmin = async record => {
      try {
        await mobileCmsAdminAPI.delete(record.id)
        message.success('Admin berhasil dihapus')
        loadAdmins()
      } catch (error) {
        const msg = error.response?.data?.result || 'Gagal menghapus admin'
        message.error(msg)
      }
    }

    onMounted(() => {
      loadAdmins()
    })

    return {
      loading,
      admins,
      searchQuery,
      pagination,
      modalVisible,
      modalLoading,
      editingAdmin,
      form,
      formRef,
      columns,
      modalTitle,
      loadAdmins,
      handleTableChange,
      showCreateModal,
      showEditModal,
      handleModalCancel,
      handleModalOk,
      deleteAdmin,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '../styleViews.scss';

.admin-management {
  padding: 0;
  background-color: white;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons .btn-delete {
  background-color: #333;
  border-color: #333;
  color: white;
}

.action-buttons .btn-delete:hover {
  background-color: #555;
  border-color: #555;
}

.btn-footer {
  height: 48px;
  padding: 0 40px;
  border-radius: 24px;
  font-weight: 700;
  font-size: 14px;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-save {
  background-color: #e11d48;
  border: 1px solid #e11d48;
  color: white;

  &:hover {
    background-color: #be123c;
    border-color: #be123c;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.btn-cancel {
  background-color: white;
  border: 2px solid #111827;
  color: #111827;

  &:hover {
    background-color: #f3f4f6;
  }
}

.btn-confirm-cancel {
  padding: 10px 20px;
  border-radius: 24px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;

  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #111827;
  color: #111827;
  width: 100%;

  &:hover {
    background: #111827;
    border: 1px solid #111827;
    color: white;
  }
}

.btn-confirm-ok {
  padding: 10px 20px;
  border-radius: 24px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;

  align-items: center;
  justify-content: center;
  background: #dc2626;
  color: white;
  border: 1px solid $primary;
  width: 100%;

  &:hover {
    background: transparent;
    color: $primary;
    border: 1px solid $primary;
  }
}
</style>
